import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { distinctUntilChanged, filter, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostCenterApportionment } from '../../../../models/cost-center-apportionment.model';
import { ICostCenter } from '../../../../models/cost-center.model';
import { IBabState } from '../../../../services/facades/master-data/bab-state.model';
import { BabFacade } from '../../../../services/facades/master-data/bab.facade';
import { JoinCostCenterCostCenterDialogComponent } from './dialog/join-cost-center-cost-center-dialog.component';

@Component({
    templateUrl: './join-cost-center-cost-center-detail.page.html',
    styleUrls: [ './join-cost-center-cost-center-detail.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinCostCenterCostCenterDetailPage implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatPaginator)
    public paginator!: MatPaginator;
    @ViewChild(MatSort)
    public sort: MatSort;
    public dataSource: MatTableDataSource<ICostCenterApportionment> = new MatTableDataSource<ICostCenterApportionment>([]);
    public state$: Observable<IBabState>;
    public selectFormGroup: FormGroup = new FormGroup({
        selectedDaterange: new FormGroup({ start: new FormControl(null), end: new FormControl(null) }),
        selectedCostCenter: new FormControl(null),
        selectedApportionmentReference: new FormControl(null),
        selectedCostAccountingType: new FormControl(null),
    });

    private destroy$: Subject<void> = new Subject();

    constructor(private myBabFacade: BabFacade, private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.state$ = this.myBabFacade.subState();
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IBabState) => s.apportionments),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((apportionments: ICostCenterApportionment[]) => {
                this.dataSource.data = apportionments;
            });
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IBabState) => s.selectedApportionmentReference),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((selectedApportionmentReference: number) => {
                this.selectFormGroup.controls[ 'selectedApportionmentReference' ].setValue(selectedApportionmentReference);
            });
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IBabState) => s.costCenterLookup),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((lookup: Record<number, ICostCenter>) => {
                this.dataSource.sortingDataAccessor = (item: ICostCenterApportionment, tableColumnName: string): string | number => {
                    switch (tableColumnName) {
                        case 'receivingCostCenterName':
                            return lookup[ item.receivingCostCenterId ].name;
                        default:
                            return item[ tableColumnName ];
                    }
                };
            });
        void this.myBabFacade.fetchData();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myBabFacade.clearState();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.listenToCostCenterSelectionChange();
        this.listenToCostAccountingTypeSelectionChange();
        this.listenToDateSelectionChange();
        this.listenToDistributionReferenceSelectionChange();
    }

    public onOpenDialog(apportionment?: ICostCenterApportionment): void {
        const dialog: MatDialogRef<JoinCostCenterCostCenterDialogComponent> = this.myMatDialog.open(JoinCostCenterCostCenterDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(apportionment) ? {} : apportionment,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myBabFacade.fetchApportionmentsIfFiltersAreValid();
            }
        });

    }

    public onDeleteApportionment(apportionment: ICostCenterApportionment): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {

            data: {
                message: 'app.join-cost-center-cost-center.confirm-delete.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myBabFacade.deleteApportionment(apportionment);
                void this.myBabFacade.fetchApportionmentsIfFiltersAreValid();
            }
        });

    }

    private listenToCostCenterSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedCostCenter' ].valueChanges.subscribe((costCenterId: number) => {
            void this.myBabFacade.setSelectedKostenstelle(costCenterId);
        });
    }

    private listenToCostAccountingTypeSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedCostAccountingType' ].valueChanges.subscribe((costAccountingTypeId: number) => {
            void this.myBabFacade.setSelectedCostAccountingType(costAccountingTypeId);
        });
    }

    private listenToDateSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedDaterange' ].valueChanges.subscribe((daterange) => {
            void this.myBabFacade.setSelectedDaterange(daterange.start, daterange.end);
        });
    }

    private listenToDistributionReferenceSelectionChange(): void {

        this.selectFormGroup.controls[ 'selectedApportionmentReference' ].valueChanges
            .pipe(filter(d => d !== null))
            .subscribe((distributionReferenceId: number) => {
                const stateSnapshot: IBabState = this.myBabFacade.getStateSnapshot();
                if (!isNullOrUndefined(stateSnapshot.selectedCostCenter)
                    && !isNullOrUndefined(stateSnapshot.selectedCostAccountingType)
                    && stateSnapshot.selectedApportionmentReference !== distributionReferenceId) {
                    void this.myBabFacade.setSelectedApportionmentReference(distributionReferenceId);
                }
            });
    }
}
